import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit } from '@angular/core'
import { Meridiem } from './keypad-time-picker/keypad-time-picker.component'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatDatepickerInputEvent } from '@angular/material/datepicker'
import { TimezoneService } from '@services/timezone.service'

/**
 * Unified DateTime Picker Component
 *
 * A reusable datetime picker that combines Angular Material's mat-datepicker
 * with a custom time picker dropdown for full datetime support.
 * Handles automatic UTC↔local timezone conversion.
 *
 * Features:
 * - Calendar dropdown for easy date selection
 * - Custom time picker with quick-select options and manual input
 * - Strict input validation - only valid dates/times can be entered
 * - Accepts/emits ISO strings (UTC) for form control values
 * - Displays dates in the browser's local timezone
 * - Supports datetime, date-only, and time-only modes
 * - Works with both formControlName and ngModel
 * - Angular Material styling with Bootstrap compatibility
 *
 * Usage:
 * <app-datetime-picker
 *   formControlName="start_ts"
 *   label="Start Time"
 *   hint="When did the event start?"
 *   [required]="true">
 * </app-datetime-picker>
 */
@Component({
    selector: 'app-datetime-picker',
    templateUrl: './datetime-picker.component.html',
    styleUrls: ['./datetime-picker.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatetimePickerComponent),
            multi: true
        }
    ]
})
export class DatetimePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
    /** Label displayed above the date input */
    @Input() label = ''

    /** Placeholder text for the input */
    @Input() placeholder = ''

    /** Hint text displayed below the input */
    @Input() hint = ''

    /** Whether the field is required */
    @Input() required = false

    /** Whether the field is disabled */
    @Input() disabled = false

    /**
     * Input mode:
     * - 'datetime': Full date and time picker (default)
     * - 'date': Date only picker
     * - 'time': Time only picker
     */
    @Input() mode: 'datetime' | 'date' | 'time' = 'datetime'

    /** Optional icon to display as prefix (Material icon name) */
    @Input() prefixIcon = ''

    /** Whether this date picker represents an end-of-day boundary (shows 11:59 PM in tooltip) */
    @Input() endOfDay = false

    /**
     * Optional initial date to pre-fill (date only, no time).
     * Use this when you want the date field pre-populated but require the user to enter the time.
     * The form value will remain null until both date AND time are filled.
     */
    @Input() initialDate: Date | string | null = null

    /**
     * Optional reference time for AM/PM defaulting.
     * When set, the time picker defaults its AM/PM to match this reference
     * instead of the current wall-clock time. Useful for end-time pickers that
     * should inherit the start time's meridiem.
     */
    @Input() referenceTime: Date | string | null = null

    /** The date value for the mat-datepicker */
    dateValue: Date | null = null

    /** Derived AM/PM from referenceTime, or null if not set */
    get referenceMeridiem(): Meridiem | null {
        if (!this.referenceTime) return null
        const d = this.referenceTime instanceof Date ? this.referenceTime : new Date(this.referenceTime)
        if (isNaN(d.getTime())) return null
        return d.getHours() >= 12 ? 'PM' : 'AM'
    }

    /** The time value in HH:mm (24h) format */
    timeValue = ''

    /** Default time string for the keypad popup (h:mm AM/PM format) */
    defaultTimeString = ''

    /** Last valid date text for reverting on invalid input */
    lastValidDateText = ''

    /** Whether the date input is focused */
    dateFocused = false

    /** Current raw text in the date input (for float label control) */
    dateText = ''

    /** Internal tracking of the full datetime */
    private internalDate: Date | null = null

    /** Callback for when value changes */
    private onChange: (value: string | null) => void = () => { }

    /** Callback for when input is touched */
    private onTouched: () => void = () => { }

    constructor(
        private timezoneService: TimezoneService,
        private elementRef: ElementRef
    ) { }

    ngOnInit(): void {
        // If initialDate is provided and no value has been written, pre-fill just the date
        const normalizedInitialDate = this.normalizeDateInput(this.initialDate)
        if (normalizedInitialDate && !this.dateValue) {
            this.dateValue = normalizedInitialDate
            this.lastValidDateText = this.formatDateAsUsDate(normalizedInitialDate)
            this.dateText = this.lastValidDateText
        }

        // Don't pre-fill a default time — the input should show the placeholder
        // until the user explicitly picks a time. The keypad popup handles its
        // own smart default internally via smartDefault().
    }

    ngOnDestroy(): void { }

    // =========================================================================
    // DATE VALIDATION HELPERS
    // =========================================================================

    /**
     * Check if a year is a leap year
     */
    private isLeapYear(year: number): boolean {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
    }

    /**
     * Get the number of days in a month
     */
    private getDaysInMonth(month: number, year: number): number {
        const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
        if (month === 2 && this.isLeapYear(year)) {
            return 29
        }
        return daysPerMonth[month - 1] || 0
    }

    /**
     * Check if a partial date string could become a valid MM/DD/YYYY date
     * Returns true if the prefix is valid so far
     */
    isValidDatePrefix(text: string): boolean {
        if (!text) return true

        // Only allow digits and forward slashes
        if (!/^[\d/]*$/.test(text)) return false

        // Check length
        if (text.length > 10) return false

        const parts = text.split('/')

        // Check month part
        if (parts[0]) {
            const monthPart = parts[0]
            if (monthPart.length > 2) return false
            if (monthPart.length === 1) {
                // First digit: 0 or 1 only
                if (!/^[01]$/.test(monthPart)) return false
            } else if (monthPart.length === 2) {
                const month = parseInt(monthPart, 10)
                if (month < 1 || month > 12) return false
            }
        }

        // Check day part
        if (parts[1] !== undefined) {
            const dayPart = parts[1]
            if (dayPart.length > 2) return false
            if (dayPart.length === 1) {
                // First digit: 0, 1, 2, or 3 only
                if (!/^[0-3]$/.test(dayPart)) return false
            } else if (dayPart.length === 2) {
                const day = parseInt(dayPart, 10)
                if (day < 1 || day > 31) return false
                // If we have a complete month, validate day against it
                if (parts[0] && parts[0].length === 2) {
                    const month = parseInt(parts[0], 10)
                    // For months with fewer days, check early
                    if (month === 2 && day > 29) return false
                    if ([4, 6, 9, 11].includes(month) && day > 30) return false
                }
            }
        }

        // Check year part
        if (parts[2] !== undefined) {
            const yearPart = parts[2]
            if (yearPart.length > 4) return false
            // Year must be digits only
            if (!/^\d*$/.test(yearPart)) return false
            // If complete year, validate range
            if (yearPart.length === 4) {
                const year = parseInt(yearPart, 10)
                if (year < 1900 || year > 2100) return false
                // Now validate full date including leap year
                if (parts[0].length === 2 && parts[1].length === 2) {
                    const month = parseInt(parts[0], 10)
                    const day = parseInt(parts[1], 10)
                    const maxDays = this.getDaysInMonth(month, year)
                    if (day > maxDays) return false
                }
            }
        }

        return true
    }

    /**
     * Check if a complete date string is a valid MM/DD/YYYY date
     */
    isValidDateText(text: string): boolean {
        if (!text || text.length !== 10) return false
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return false

        const parsed = this.parseUsDateStrict(text)
        return parsed !== null
    }

    /**
     * Parse a MM/DD/YYYY string strictly without using new Date(string)
     * Returns a Date object or null if invalid
     */
    parseUsDateStrict(text: string): Date | null {
        if (!text || text.length !== 10) return null

        const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if (!match) return null

        const month = parseInt(match[1], 10)
        const day = parseInt(match[2], 10)
        const year = parseInt(match[3], 10)

        // Validate ranges
        if (month < 1 || month > 12) return null
        if (day < 1) return null
        if (year < 1900 || year > 2100) return null

        // Validate day for the given month/year
        const maxDays = this.getDaysInMonth(month, year)
        if (day > maxDays) return null

        // Create date using numeric constructor (not string parsing)
        const date = new Date(year, month - 1, day)

        // Verify the date was created correctly (handles edge cases)
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return null
        }

        return date
    }

    /**
     * Format a Date object as MM/DD/YYYY
     */
    formatDateAsUsDate(date: Date): string {
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const year = date.getFullYear().toString()
        return `${month}/${day}/${year}`
    }

    // =========================================================================
    // TIME VALIDATION HELPERS
    // =========================================================================

    /**
     * Check if a partial time string could become a valid h:mm AM/PM time
     * Returns true if the prefix is valid so far
     */
    isValidTimePrefix(text: string): boolean {
        if (!text) return true

        // Normalize for checking
        const upper = text.toUpperCase()

        // Only allow: digits, colon, space, A, P, M
        if (!/^[\d: APMapm]*$/.test(text)) return false

        // Max reasonable length: "12:59 PM" = 8 chars
        if (text.length > 8) return false

        // Parse the structure
        const colonIndex = upper.indexOf(':')

        if (colonIndex === -1) {
            // No colon yet - should be hour digits only (1-12)
            const hourPart = upper.replace(/[ APM]/g, '')
            if (hourPart.length > 2) return false
            if (hourPart.length === 1) {
                if (!/^[1-9]$/.test(hourPart)) return false
            } else if (hourPart.length === 2) {
                const hour = parseInt(hourPart, 10)
                if (hour < 1 || hour > 12) return false
            }
            // Check AM/PM part if present without colon (not valid structure)
            if (/[APM]/.test(upper) && colonIndex === -1) {
                // Allow partial AM/PM only after complete time
                return false
            }
        } else {
            // Has colon
            const hourPart = upper.substring(0, colonIndex)
            const afterColon = upper.substring(colonIndex + 1)

            // Validate hour
            if (hourPart.length < 1 || hourPart.length > 2) return false
            const hour = parseInt(hourPart, 10)
            if (isNaN(hour) || hour < 1 || hour > 12) return false

            // Parse minutes and AM/PM from afterColon
            const minuteMatch = afterColon.match(/^(\d{0,2})(.*)$/)
            if (!minuteMatch) return false

            const minutePart = minuteMatch[1]
            const periodPart = minuteMatch[2].trim()

            // Validate minute prefix
            if (minutePart.length === 1) {
                if (!/^[0-5]$/.test(minutePart)) return false
            } else if (minutePart.length === 2) {
                const minute = parseInt(minutePart, 10)
                if (minute < 0 || minute > 59) return false
            }

            // Validate period part
            if (periodPart) {
                // Must be a valid prefix of " AM" or " PM" or "AM" or "PM"
                if (!/^[ ]?[AP]?[M]?$/i.test(periodPart)) return false
            }
        }

        return true
    }

    /**
     * Parse a complete time string strictly (h:mm AM/PM format)
     * Returns HH:mm (24-hour) string or null if invalid
     * If AM/PM is omitted, defaults to AM
     */
    parseTimeStrict(text: string): string | null {
        if (!text) return null

        const normalized = text.trim().toUpperCase()

        // Match: h:mm AM, hh:mm AM, h:mm PM, hh:mm PM, or h:mm / hh:mm (no period = default AM)
        const match = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/)
        if (!match) return null

        const hour = parseInt(match[1], 10)
        const minute = parseInt(match[2], 10)
        // Smart AM/PM default: if omitted, use reference meridiem or current time
        let period = match[3]
        if (!period) {
            period = this.referenceMeridiem ?? (new Date().getHours() >= 12 ? 'PM' : 'AM')
        }

        // Validate ranges
        if (hour < 1 || hour > 12) return null
        if (minute < 0 || minute > 59) return null

        // Convert to 24-hour
        let hour24 = hour
        if (period === 'AM') {
            if (hour === 12) hour24 = 0
        } else {
            if (hour !== 12) hour24 += 12
        }

        return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
    }

    /**
     * Format a 24-hour time string as h:mm AM/PM
     */
    formatTimeAs12Hour(time24: string): string {
        if (!time24) return ''

        const [hours, minutes] = time24.split(':').map(Number)
        const period = hours >= 12 ? 'PM' : 'AM'
        const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`
    }

    // =========================================================================
    // DATE INPUT HANDLERS
    // =========================================================================

    /**
     * Handle keydown on date input - block invalid keys
     */
    onDateKeydown(event: KeyboardEvent): void {
        // Allow control keys
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
        if (allowedKeys.includes(event.key)) return

        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if (event.ctrlKey || event.metaKey) return

        // Only allow digits and forward slash
        if (!/^[\d/]$/.test(event.key)) {
            event.preventDefault()
            return
        }

        // Simulate what the input would look like after this key
        const input = event.target as HTMLInputElement
        const start = input.selectionStart ?? 0
        const end = input.selectionEnd ?? 0
        const currentValue = input.value
        const newValue = currentValue.substring(0, start) + event.key + currentValue.substring(end)

        // Check if the new value is a valid prefix
        if (!this.isValidDatePrefix(newValue)) {
            event.preventDefault()
        }
    }

    /**
     * Handle input on date field - sanitize and validate
     */
    onDateInput(event: Event): void {
        const input = event.target as HTMLInputElement
        let value = input.value

        // Remove any invalid characters
        value = value.replace(/[^\d/]/g, '')

        // Check if valid prefix
        if (!this.isValidDatePrefix(value)) {
            // Revert to last valid
            input.value = this.lastValidDateText
            this.dateText = this.lastValidDateText
            return
        }

        // Auto-insert slashes
        if (value.length === 2 && !value.includes('/')) {
            value = value + '/'
        } else if (value.length === 5 && value.indexOf('/') === 2 && value.lastIndexOf('/') === 2) {
            value = value + '/'
        }

        // Update input if we added slashes
        if (input.value !== value) {
            input.value = value
        }

        // Sync dateText for floatLabel binding
        this.dateText = value

        // Check if complete and valid
        if (this.isValidDateText(value)) {
            const parsed = this.parseUsDateStrict(value)
            if (parsed) {
                this.lastValidDateText = value
                this.dateValue = parsed
                this.internalDate = new Date(parsed)
                if (this.timeValue) {
                    const [hours, minutes] = this.timeValue.split(':').map(Number)
                    this.internalDate.setHours(hours, minutes, 0, 0)
                }
                this.emitCombinedValue()
            }
        }
    }

    /**
     * Handle paste on date input - sanitize pasted content
     */
    onDatePaste(event: ClipboardEvent): void {
        event.preventDefault()
        const pastedText = event.clipboardData?.getData('text') || ''

        // Clean the pasted text - keep only digits and slashes
        const cleaned = pastedText.replace(/[^\d/]/g, '').substring(0, 10)

        if (this.isValidDatePrefix(cleaned)) {
            const input = event.target as HTMLInputElement
            const start = input.selectionStart ?? 0
            const end = input.selectionEnd ?? 0
            const currentValue = input.value
            const newValue = currentValue.substring(0, start) + cleaned + currentValue.substring(end)

            if (this.isValidDatePrefix(newValue.substring(0, 10))) {
                input.value = newValue.substring(0, 10)
                // Trigger input handling
                this.onDateInput({ target: input } as unknown as Event)
            }
        }
    }

    /**
     * Handle blur on date input - validate and revert if invalid
     */
    onDateInputBlur(event: FocusEvent): void {
        const input = event.target as HTMLInputElement
        const value = input.value.trim()

        // If empty, clear the date
        if (!value) {
            this.dateValue = null
            this.internalDate = null
            this.lastValidDateText = ''
            this.dateText = ''
            this.emitCombinedValue()
            this.onTouched()
            return
        }

        // Must be a complete valid date
        if (this.isValidDateText(value)) {
            const parsed = this.parseUsDateStrict(value)
            if (parsed) {
                this.lastValidDateText = value
                this.dateText = value
                this.dateValue = parsed
                this.internalDate = new Date(parsed)
                if (this.timeValue) {
                    const [hours, minutes] = this.timeValue.split(':').map(Number)
                    this.internalDate.setHours(hours, minutes, 0, 0)
                }
                this.emitCombinedValue()
            }
        } else {
            // Invalid or incomplete - revert to last valid
            input.value = this.lastValidDateText
            this.dateText = this.lastValidDateText
        }
        this.onTouched()
    }

    // =========================================================================
    // TIME INPUT HANDLERS (keypad popup)
    // =========================================================================

    /**
     * Called when the keypad popup confirms a time. The keypad emits a
     * "h:mm AM/PM" string. We parse it back to 24h, refresh the input
     * element directly (the keypad is a one-shot emitter, not a two-way
     * binding), and update the combined datetime.
     */
    onKeypadTimeSet(timeString: string, input: HTMLInputElement): void {
        if (!timeString) {
            this.clearTime(input)
            return
        }

        const parsed = this.parseTimeStrict(timeString)
        if (parsed) {
            this.timeValue = parsed
            this.defaultTimeString = this.formatTimeAs12Hour(parsed)
            input.value = this.defaultTimeString
            this.applyTimeToDate()
            this.onTouched()
        }
    }

    /**
     * Clear the time value and reset the input element.
     * Sets defaultTimeString to '' so the [value] binding evaluates to ''
     * and the placeholder shows. The keypad's smartDefault() handles its
     * own initial selection independently.
     */
    clearTime(input: HTMLInputElement): void {
        this.timeValue = ''
        this.defaultTimeString = ''
        input.value = ''
        this.applyTimeToDate()
        this.onTouched()
    }

    /**
     * Get a smart default time string (nearest quarter-hour of now).
     * Used when the clock face opens with no value set — defaults to current period (AM/PM).
     */
    private getSmartDefaultTime(): string {
        const now = new Date()
        const minutes = now.getMinutes()
        const roundedMinutes = Math.round(minutes / 15) * 15
        const roundedDate = new Date(now)
        roundedDate.setMinutes(roundedMinutes, 0, 0)
        if (roundedMinutes === 60) {
            roundedDate.setHours(roundedDate.getHours() + 1, 0, 0, 0)
        }
        return this.formatTimeAs12Hour(
            `${roundedDate.getHours().toString().padStart(2, '0')}:${roundedDate.getMinutes().toString().padStart(2, '0')}`
        )
    }

    /**
     * Handle blur on the time text input — validate what the user typed.
     * If valid, update the time. If empty, clear. If invalid, revert.
     */
    /**
     * On input, auto-append smart AM/PM when user types a complete time without it.
     * This ensures the directive sees "3:45 PM" instead of bare "3:45".
     */
    onTimeTextInput(event: Event): void {
        const input = event.target as HTMLInputElement
        const value = input.value.trim()

        // If user just typed a complete h:mm or hh:mm without AM/PM, append it
        if (/^\d{1,2}:\d{2}$/.test(value)) {
            const smartPeriod = this.referenceMeridiem ?? (new Date().getHours() >= 12 ? ' PM' : ' AM')
            input.value = value + (smartPeriod.startsWith(' ') ? smartPeriod : ' ' + smartPeriod)
        }
    }

    onTimeTextBlur(value: string, input: HTMLInputElement): void {
        const trimmed = value.trim()

        // Empty — treat as cleared (show placeholder, don't restore smart default)
        if (!trimmed) {
            this.timeValue = ''
            this.defaultTimeString = ''
            input.value = ''
            this.applyTimeToDate()
            this.onTouched()
            return
        }

        // Try parsing (should already have AM/PM from onTimeTextInput)
        const parsed = this.parseTimeStrict(trimmed)
        if (parsed) {
            this.timeValue = parsed
            const formatted = this.formatTimeAs12Hour(parsed)
            this.defaultTimeString = formatted
            input.value = formatted
            input.dispatchEvent(new Event('change', { bubbles: true }))
            this.applyTimeToDate()
        } else {
            // Invalid — revert to last good value or default
            input.value = this.displayTime || this.defaultTimeString
        }
        this.onTouched()
    }

    // =========================================================================
    // DISPLAY GETTERS
    // =========================================================================

    /**
     * Get the display time in 12-hour format (e.g., "2:30 PM")
     */
    get displayTime(): string {
        return this.formatTimeAs12Hour(this.timeValue)
    }

    /**
     * Get the display date in MM/DD/YYYY format
     */
    get displayDate(): string {
        if (!this.dateValue) return ''
        return this.formatDateAsUsDate(this.dateValue)
    }

    /**
     * Get a descriptive tooltip string for the current value.
     * - datetime mode: "Wednesday, February 4, 2026 at 2:30 PM"
     * - date mode: "Wednesday, February 4, 2026 at 12:00 AM" (or 11:59 PM if endOfDay)
     */
    get tooltipText(): string {
        if (this.mode === 'date') {
            if (!this.dateValue) return ''
            // For end-of-day pickers, show 11:59 PM to reflect the actual query boundary
            const displayDate = new Date(this.dateValue)
            if (this.endOfDay) {
                displayDate.setHours(23, 59, 0, 0)
            }
            return displayDate.toLocaleString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
        }

        if (this.mode === 'datetime') {
            if (!this.internalDate) return ''
            return this.internalDate.toLocaleString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
        }

        // time mode
        if (!this.timeValue) return ''
        return this.formatTimeAs12Hour(this.timeValue)
    }

    // =========================================================================
    // DATE PICKER HANDLERS
    // =========================================================================

    /**
     * Called when date picker toggle is clicked - close time picker
     */
    onDatePickerToggle(): void { }

    onDatePickerOpened(): void { }

    /**
     * Called when date picker closes
     */
    onDatePickerClosed(): void {
        // Sync the text input with the picker value
        if (this.dateValue) {
            this.lastValidDateText = this.formatDateAsUsDate(this.dateValue)
        }
    }

    /**
     * Handle date changes from the mat-datepicker (datetime mode)
     */
    onDateChange(event: MatDatepickerInputEvent<Date>): void {
        const newDate = event.value
        if (!newDate) {
            this.dateValue = null
            this.lastValidDateText = ''
            this.emitCombinedValue()
            return
        }

        // Check if time is currently empty/null
        const timeIsEmpty = !this.timeValue

        // Keep the existing time or auto-fill based on selected date
        let hours = this.internalDate?.getHours() ?? 0
        let minutes = this.internalDate?.getMinutes() ?? 0

        if (timeIsEmpty) {
            // Auto-fill time when date is selected and time is empty
            const now = new Date()
            const isToday = newDate.getFullYear() === now.getFullYear() &&
                newDate.getMonth() === now.getMonth() &&
                newDate.getDate() === now.getDate()

            if (isToday) {
                // Use current time for today
                hours = now.getHours()
                minutes = now.getMinutes()
            } else {
                // Use 8:00 AM as default for other dates
                hours = 8
                minutes = 0
            }

            // Update timeValue and selectors to reflect auto-filled time
            this.timeValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
            this.defaultTimeString = this.formatTimeAs12Hour(this.timeValue)
        }

        this.internalDate = new Date(
            newDate.getFullYear(),
            newDate.getMonth(),
            newDate.getDate(),
            hours,
            minutes
        )
        this.dateValue = this.internalDate
        this.lastValidDateText = this.formatDateAsUsDate(this.dateValue)
        this.emitCombinedValue()
    }


    /**
     * Apply current time to internal date
     * In datetime mode, if time is empty, emit null (incomplete datetime)
     */
    private applyTimeToDate(): void {
        if (!this.timeValue) {
            // In datetime mode, both date and time are required for a valid value
            if (this.mode === 'datetime') {
                this.onChange(null)
                return
            }
            if (this.internalDate) {
                this.internalDate.setHours(0, 0, 0, 0)
            }
            this.emitCombinedValue()
            return
        }

        const [hours, minutes] = this.timeValue.split(':').map(Number)

        if (this.internalDate) {
            this.internalDate = new Date(this.internalDate)
            this.internalDate.setHours(hours, minutes, 0, 0)
        } else {
            this.internalDate = new Date()
            this.internalDate.setHours(hours, minutes, 0, 0)
            this.dateValue = this.internalDate
            this.lastValidDateText = this.formatDateAsUsDate(this.dateValue)
        }

        this.emitCombinedValue()
    }

    // =========================================================================
    // DATE-ONLY MODE HANDLERS
    // =========================================================================

    /**
     * Handle date-only change (date mode)
     */
    onDateOnlyChange(event: MatDatepickerInputEvent<Date>): void {
        const newDate = event.value
        if (!newDate) {
            this.dateValue = null
            this.lastValidDateText = ''
            this.onChange(null)
            return
        }

        this.dateValue = newDate
        this.lastValidDateText = this.formatDateAsUsDate(newDate)
        // Emit as ISO date string YYYY-MM-DD
        const year = newDate.getFullYear()
        const month = (newDate.getMonth() + 1).toString().padStart(2, '0')
        const day = newDate.getDate().toString().padStart(2, '0')
        this.onChange(`${year}-${month}-${day}`)
    }

    // =========================================================================
    // TIME-ONLY MODE HANDLERS
    // =========================================================================

    /**
     * Handle time-only change (time mode) - uses native time input
     */
    onTimeOnlyChange(event: Event): void {
        const input = event.target as HTMLInputElement
        const value = input.value

        if (!value) {
            this.timeValue = ''
            this.onChange(null)
            return
        }

        // Native time input always gives valid HH:mm
        this.timeValue = value
        this.onChange(value)
    }

    // =========================================================================
    // VALUE EMISSION
    // =========================================================================

    /**
     * Emit the combined datetime value
     * - datetime mode: emits UTC ISO string, requires both date and time
     * - date mode: emits YYYY-MM-DD string (timezone-agnostic calendar date)
     */
    private emitCombinedValue(): void {
        if (!this.internalDate) {
            this.onChange(null)
            return
        }

        // In datetime mode, both date and time are required for a valid value
        if (this.mode === 'datetime' && !this.timeValue) {
            this.onChange(null)
            return
        }

        // Date-only mode: emit YYYY-MM-DD (no timezone conversion)
        if (this.mode === 'date') {
            const year = this.internalDate.getFullYear()
            const month = (this.internalDate.getMonth() + 1).toString().padStart(2, '0')
            const day = this.internalDate.getDate().toString().padStart(2, '0')
            this.onChange(`${year}-${month}-${day}`)
            return
        }

        // Convert local datetime to UTC ISO string
        const utcValue = this.internalDate.toISOString()
        this.onChange(utcValue)
    }

    /**
     * Handle blur event
     */
    onBlur(): void {
        this.onTouched()
    }

    // =========================================================================
    // CONTROL VALUE ACCESSOR IMPLEMENTATION
    // =========================================================================

    /**
     * Write value from form control to the input
     * Converts UTC ISO string to local date and time values for display
     */
    writeValue(value: string | Date | null): void {
        if (!value) {
            // If initialDate is provided, pre-fill just the date (time remains empty)
            const normalizedInitialDate = this.normalizeDateInput(this.initialDate)
            if (normalizedInitialDate) {
                this.dateValue = normalizedInitialDate
                this.lastValidDateText = this.formatDateAsUsDate(normalizedInitialDate)
                this.dateText = this.lastValidDateText
                // Don't set timeValue, internalDate - form value stays null until time is filled
                this.timeValue = ''
                this.internalDate = null
                this.defaultTimeString = ''
                return
            }
            this.dateValue = null
            this.timeValue = ''
            this.internalDate = null
            this.lastValidDateText = ''
            this.defaultTimeString = ''
            this.dateText = ''
            return
        }

        // Parse the incoming value to a local Date
        let date: Date
        if (value instanceof Date) {
            date = value
        } else {
            // Check if this is a date-only string (YYYY-MM-DD) — must parse as local time,
            // not UTC. new Date("2026-02-05") creates UTC midnight which shifts back a day
            // in US timezones, causing an off-by-one display bug.
            const dateOnlyMatch = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null
            if (dateOnlyMatch) {
                date = new Date(parseInt(dateOnlyMatch[1]), parseInt(dateOnlyMatch[2]) - 1, parseInt(dateOnlyMatch[3]))
            } else {
                date = new Date(value)
            }
        }

        if (isNaN(date.getTime())) {
            this.dateValue = null
            this.timeValue = ''
            this.internalDate = null
            this.lastValidDateText = ''
            this.defaultTimeString = ''
            this.dateText = ''
            return
        }

        this.internalDate = date
        this.dateValue = date
        this.lastValidDateText = this.formatDateAsUsDate(date)
        this.dateText = this.lastValidDateText

        // Format time as HH:mm for the time input
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        this.timeValue = `${hours}:${minutes}`
        this.defaultTimeString = this.formatTimeAs12Hour(this.timeValue)
    }

    private normalizeDateInput(value: Date | string | null | undefined): Date | null {
        if (!value) {
            return null
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value
        }

        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? null : parsed
    }

    /**
     * Register onChange callback
     */
    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn
    }

    /**
     * Register onTouched callback
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    /**
     * Set disabled state
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }
}
